import React from "react";
import { Calendar, Check, ChevronRight, DollarSign, Minus, MessageSquareText, Plus } from "lucide-react";

import { Button } from "../../../../components/ui/button";
import { useScriptTextSize } from "../../hooks/useScriptTextSize";
import { SCRIPT_BLOCKS_FIXTURE } from "../../workspaceFixtures";
import { WORKSPACE_TOOL_PROJECTION_STATUSES } from "../../projectionContract";
import { SCRIPT_RUN_ADMISSION_STATUSES } from "../../workspaceViewModel";
import ToolProjectionNotice from "./ToolProjectionNotice";

const DEFAULT_PROJECTION_STATUS = Object.freeze({
  status: WORKSPACE_TOOL_PROJECTION_STATUSES.OK,
  reason: null,
});

// Fail-closed, NOT "attached". An unprovided runAdmission means this
// component is not actually wired to the composed fake runtime (a direct/
// isolated render, or broken wiring upstream) — it must not silently
// synthesize an authoritative attached Run identity to fill that gap
// (RUN-RUNTIME-001 §33 Creation Unknown: "a new identity is prohibited
// until the outcome is known"; §38: "silent retirement substitution...
// prohibited"; WORKSPACE-RUNTIME-001 §105: "Unknown admission or
// transition makes execution read-only until reconciled"; ENG-008 §4:
// "Missing or prohibited information is not inferred from another
// domain"). The default demo's "attached" appearance comes only from the
// real chain: fakeRuntime.js's BASELINE explicitly provides a resolved
// assignment + attached Run, and AgentWorkspaceShell always passes the
// composed result down — this default is never reached by that path.
const DEFAULT_RUN_ADMISSION = Object.freeze({
  status: SCRIPT_RUN_ADMISSION_STATUSES.UNKNOWN,
  reason: "admission-not-provided",
  candidate: null,
  run: null,
});

function ScriptCandidateBadge() {
  return (
    <span
      className="shrink-0 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-200 ring-1 ring-amber-400/30"
      data-testid="workspace-script-candidate-badge"
    >
      Candidate
    </span>
  );
}

// WORKSPACE-RUNTIME-001 §105: "After authoritative attachment, it renders
// the immutable publication and current Run cursor." A compact, subtle
// identity strip -- not a form, not editable, matching the panel's
// fully-disabled/read-only posture.
function ScriptAttachedRunBadge({ run }) {
  return (
    <span
      className="shrink-0 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-200 ring-1 ring-emerald-400/20"
      data-testid="workspace-script-attached-run-badge"
      title={`Run ${run.runId} · Publication ${run.publicationVersion} · Cursor ${run.cursor.blockKey}`}
    >
      Run {run.runId}
    </span>
  );
}

function RequiredBadge() {
  return (
    <span className="rounded-full border border-indigo-400/30 bg-indigo-400/10 px-2 py-0.5 text-[11px] font-medium text-indigo-200">
      Required
    </span>
  );
}

function ResponseChoice({ label, selected }) {
  return (
    <button
      type="button"
      disabled
      className={
        selected
          ? "rounded-full bg-indigo-500/90 px-4 py-1.5 text-sm font-medium text-white disabled:opacity-100"
          : "rounded-full bg-white/[0.06] px-4 py-1.5 text-sm font-medium text-slate-300 disabled:opacity-100"
      }
    >
      {label}
    </button>
  );
}

function QuestionInput({ question }) {
  if (question.type === "choice") {
    return (
      <div className="flex flex-wrap gap-2" role="group" aria-label={question.prompt}>
        {question.options.map((option) => (
          <ResponseChoice key={option} label={option} />
        ))}
      </div>
    );
  }

  const Icon = question.type === "date" ? Calendar : DollarSign;
  return (
    <div className="relative">
      <Icon
        className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500"
        aria-hidden="true"
      />
      <input
        type="text"
        disabled
        placeholder={question.placeholder}
        className="h-9 w-full max-w-xs rounded-[8px] border border-white/10 bg-transparent pl-8 pr-3 text-sm text-slate-300 placeholder:text-slate-600 disabled:opacity-80"
      />
    </div>
  );
}

/**
 * Script text-size controls. Mirrors the established Script Runner
 * ReaderFontControls button treatment (frontend/src/pages/script-play/
 * components/ReaderFontControls.jsx) — same size/shape/tone — with
 * accessible names and a disabled state added at each bound, which the
 * source component does not provide.
 */
function ScriptTextSizeControls({ onDecrease, onIncrease, canDecrease, canIncrease }) {
  const buttonClass = "h-8 w-8 rounded-full bg-white/10 text-slate-100 ring-1 ring-white/10 hover:bg-white/15";

  return (
    <div className="flex items-center gap-1" data-testid="workspace-script-text-size-controls">
      <Button
        type="button"
        variant="secondary"
        size="icon"
        aria-label="Decrease script text size"
        title={canDecrease ? "Decrease script text size" : "Minimum text size reached"}
        disabled={!canDecrease}
        onClick={onDecrease}
        className={buttonClass}
        data-testid="workspace-script-text-decrease"
      >
        <Minus className="h-4 w-4" aria-hidden="true" />
      </Button>
      <Button
        type="button"
        variant="secondary"
        size="icon"
        aria-label="Increase script text size"
        title={canIncrease ? "Increase script text size" : "Maximum text size reached"}
        disabled={!canIncrease}
        onClick={onIncrease}
        className={buttonClass}
        data-testid="workspace-script-text-increase"
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
      </Button>
    </div>
  );
}

/**
 * A Script Block: the established MissionAssist unit for grouping related
 * spoken content, questions, response choices, and inputs (see Script
 * Builder / Script Runner). Read-only presentation only — no execution.
 * Readable prose (spoken lines, acknowledgment, question prompts) responds
 * to the text-size controls; interactive control chrome (badges, pills,
 * the numbered index) stays at its fixed UI size, matching Script Runner.
 */
function ScriptBlock({ block, fontSize }) {
  return (
    <div
      className="flex flex-col gap-4 rounded-2xl border border-white/10 bg-white/5 p-4"
      data-testid={`workspace-script-block-${block.key}`}
    >
      <div className="flex items-center gap-2">
        <MessageSquareText className="h-4 w-4 text-indigo-300" aria-hidden="true" />
        <p className="text-sm font-semibold text-slate-100">{block.title}</p>
      </div>

      {block.lines?.length ? (
        <div className="flex max-w-prose flex-col gap-3 leading-relaxed text-slate-200" style={{ fontSize }}>
          {block.lines.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      ) : null}

      {block.responses?.length ? (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium text-slate-500">{block.responseLabel}</p>
          <div className="flex flex-wrap gap-2" role="group" aria-label={block.responseLabel}>
            {block.responses.map((response) => (
              <ResponseChoice key={response.key} label={response.label} selected={response.selected} />
            ))}
          </div>
        </div>
      ) : null}

      {block.acknowledgment ? (
        <div className="flex items-center gap-2 text-emerald-300" style={{ fontSize }}>
          <Check className="h-4 w-4 shrink-0" aria-hidden="true" />
          {block.acknowledgment}
        </div>
      ) : null}

      {block.questions?.length ? (
        <div className="flex flex-col gap-4">
          {block.questions.map((question) => (
            <div key={question.key} className="flex flex-col gap-2.5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2.5">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-500/80 text-[11px] font-semibold text-white">
                    {question.number}
                  </span>
                  <p className="font-medium text-slate-200" style={{ fontSize }}>
                    {question.prompt}
                  </p>
                </div>
                {question.required ? <RequiredBadge /> : null}
              </div>
              <QuestionInput question={question} />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Script tab: a Script working surface (the "Your Reply"-equivalent
 * container from Script Runner) hosting the Script Blocks, so blocks read
 * as content inside one working surface rather than floating directly on
 * the Main Card background. The working surface (overflow-hidden) contains
 * a dedicated Script Blocks region that owns its own vertical scroll —
 * blocks can never render past the working surface's own border. This is a
 * second, independent scroll boundary nested inside the Main Card's own
 * active-tab-body boundary. Deterministic fixture content only — every
 * control remains disabled; no progression, persistence, execution, or Run
 * behavior exists.
 *
 * Accepts `projectionStatus` (WORKSPACE-AUDIT-007 §13 item 4 — outer
 * Account-context gate) and `runAdmission` (item 6 — the candidate-vs-
 * attached Script/Run admission state, WORKSPACE-RUNTIME-001 §105).
 * `projectionStatus` is checked first: a foreign/invalid Account context
 * is never disclosed by falling through to admission-specific detail.
 * Once past that gate, `runAdmission.status` selects one of five renders:
 * missing/pending/unknown (a ToolProjectionNotice, shared with the other
 * five tools) or candidate/attached (Script Block content, labeled
 * accordingly). Content still comes from SCRIPT_BLOCKS_FIXTURE either
 * way — no execution, navigation, or response-submission behavior exists
 * in either state; only the identity/labeling differs.
 */
export default function ScriptPanel({
  projectionStatus = DEFAULT_PROJECTION_STATUS,
  runAdmission = DEFAULT_RUN_ADMISSION,
}) {
  const fixture = SCRIPT_BLOCKS_FIXTURE;
  const { fontSize, increase, decrease, canIncrease, canDecrease } = useScriptTextSize();

  if (projectionStatus.status !== WORKSPACE_TOOL_PROJECTION_STATUSES.OK) {
    return (
      <ToolProjectionNotice
        status={projectionStatus.status}
        reason={projectionStatus.reason}
        testIdPrefix="workspace-script"
      />
    );
  }

  if (
    runAdmission.status === SCRIPT_RUN_ADMISSION_STATUSES.MISSING ||
    runAdmission.status === SCRIPT_RUN_ADMISSION_STATUSES.PENDING ||
    runAdmission.status === SCRIPT_RUN_ADMISSION_STATUSES.UNKNOWN
  ) {
    return (
      <ToolProjectionNotice status={runAdmission.status} reason={runAdmission.reason} testIdPrefix="workspace-script" />
    );
  }

  const isCandidate = runAdmission.status === SCRIPT_RUN_ADMISSION_STATUSES.CANDIDATE;

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-3 p-5" data-testid="workspace-script-panel">
      <div className="flex shrink-0 items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <p className="text-xs text-slate-500">Script: {fixture.scriptType}</p>
          {isCandidate ? <ScriptCandidateBadge /> : <ScriptAttachedRunBadge run={runAdmission.run} />}
        </div>
        <ScriptTextSizeControls
          onDecrease={decrease}
          onIncrease={increase}
          canDecrease={canDecrease}
          canIncrease={canIncrease}
        />
      </div>

      <div
        className="flex flex-1 min-h-0 flex-col overflow-hidden rounded-2xl border border-white/10 bg-black/20 p-4"
        data-testid="workspace-script-working-surface"
      >
        <div
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto"
          data-testid="workspace-script-blocks-region"
        >
          {fixture.blocks.map((block) => (
            <ScriptBlock key={block.key} block={block} fontSize={fontSize} />
          ))}

          <div className="flex items-center gap-1 text-xs font-medium text-slate-500">
            <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
            {fixture.nextSectionLabel}
          </div>
        </div>
      </div>
    </div>
  );
}
